import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { LayoutGrid, Settings2, ShieldCheck, ListOrdered, History, Loader2, CheckCircle2 } from 'lucide-react';
import NodePalette from './NodePalette';

const SECTIONS = [
  { id: 'steps', label: 'Steps', icon: LayoutGrid },
  { id: 'general', label: 'General', icon: Settings2 },
  { id: 'failsafe', label: 'Failsafe', icon: ShieldCheck },
  { id: 'live', label: 'Wallboard', icon: ListOrdered },
  { id: 'versions', label: 'Versions', icon: History },
];
const input = 'w-full px-3 py-2 rounded-lg text-sm border focus:outline-none focus:border-teal-400/60';
const inputStyle = { background: 'var(--vox-node-bg)', borderColor: 'var(--vox-chrome-border)', color: 'var(--vox-text-primary)' };

const Label = ({ children }) => <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'var(--vox-text-muted)' }}>{children}</div>;
const Muted = ({ children }) => <p className="text-[11px] leading-snug mt-1.5" style={{ color: 'var(--vox-text-secondary)' }}>{children}</p>;
const Switch = ({ on, onChange, label, testid }) => (
  <button type="button" onClick={() => onChange(!on)} className="w-full flex items-center justify-between py-1.5 text-left" data-testid={testid}>
    <span className="text-xs" style={{ color: 'var(--vox-text-primary)' }}>{label}</span>
    <span className={`w-9 h-5 rounded-full p-0.5 transition-colors ${on ? 'bg-teal-400' : 'bg-slate-400/40'}`}><span className={`block w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`} /></span>
  </button>
);

const Stats = ({ api, subAccountId, flowId }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/stats?days=7`).then(r => setD(r.data)).catch(() => {}); }, [api, subAccountId, flowId]);
  if (!d) return null;
  const tiles = [['Calls', d.calls], ['Menu drop-offs', d.menu_dropoffs], ['Abandoned', d.abandoned], ['Callbacks', d.callbacks], ['Max-wait exits', d.timeouts], ['Avg wait', `${Math.round(d.avg_wait_sec / 60)}m`]];
  return (<div className="mt-5" data-testid="rail-stats"><Label>Last 7 days</Label>
    <div className="grid grid-cols-3 gap-2 mb-3">{tiles.map(([l, v]) => <div key={l} className="rounded-lg border py-2 text-center" style={{ borderColor: 'var(--vox-chrome-border)' }}><div className="text-sm font-semibold tabular-nums" style={{ color: 'var(--vox-text-primary)' }}>{v}</div><div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--vox-text-muted)' }}>{l}</div></div>)}</div>
    <Label>Menu choices</Label>{d.menu_choices.length === 0 ? <Muted>No menu choices yet.</Muted> : d.menu_choices.map(c => {
      const max = d.menu_choices[0].count || 1;
      return <div key={c.label} className="mb-1.5"><div className="flex justify-between text-[11px]" style={{ color: 'var(--vox-text-primary)' }}><span>{c.label}</span><span className="tabular-nums">{c.count}</span></div><div className="h-1.5 rounded bg-slate-400/20"><div className="h-1.5 rounded bg-teal-400" style={{ width: `${(c.count / max) * 100}%` }} /></div></div>; })}
  </div>);
};

const Versions = ({ api, subAccountId, flowId, onRestored, refreshKey }) => {
  const [list, setList] = useState(null);
  useEffect(() => { api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/versions`).then(r => setList(r.data)).catch(() => setList([])); }, [api, subAccountId, flowId, refreshKey]);
  const restore = async (v) => {
    const r = await api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/versions/${v}/restore`);
    onRestored?.(r.data); toast.success(`Version ${v} loaded. Publish to make it live.`);
  };
  if (!list) return <Muted>Loading…</Muted>;
  return (<div data-testid="rail-versions">{list.length === 0 ? <Muted>No published versions yet. Click Publish in the header to make your first one.</Muted> : list.map(v => (
    <div key={v.version} className="flex items-center justify-between py-2 border-b text-xs" style={{ borderColor: 'var(--vox-chrome-border)', color: 'var(--vox-text-primary)' }} data-testid={`version-row-${v.version}`}>
      <div><div className="font-semibold">Version {v.version}</div><div className="text-[10px]" style={{ color: 'var(--vox-text-muted)' }}>{new Date(v.published_at).toLocaleString()} · {v.steps} steps</div></div>
      <button onClick={() => restore(v.version)} className="text-[11px] text-teal-400 hover:underline" data-testid={`version-restore-${v.version}`}>Restore</button>
    </div>))}</div>);
};

const LiveQueue = ({ api, subAccountId, flowId }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/queue`).then(r => alive && setD(r.data)).catch(() => {});
    load(); const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [api, subAccountId, flowId]);
  if (!d) return <Muted>Loading…</Muted>;
  return (
    <div data-testid="rail-live-queue">
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        {[['AI calls now', d.live], ['Call limit', d.cap], ['Waiting', d.entries.length]].map(([l, v]) => (
          <div key={l} className="rounded-lg border py-2" style={{ borderColor: 'var(--vox-chrome-border)' }}>
            <div className="text-base font-semibold tabular-nums" style={{ color: 'var(--vox-text-primary)' }}>{v}</div>
            <div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--vox-text-muted)' }}>{l}</div>
          </div>))}
      </div>
      {d.entries.length === 0 ? <Muted>Nobody is waiting right now.</Muted> : d.entries.map(e => (
        <div key={e.entry_id} className="flex items-center justify-between py-2 border-b text-xs" style={{ borderColor: 'var(--vox-chrome-border)', color: 'var(--vox-text-primary)' }} data-testid={`live-queue-entry-${e.position}`}>
          <span className="font-semibold">#{e.position}</span>
          <span className="tabular-nums">•••{(e.from_number || '').slice(-4)}</span>
          <span className={e.status === 'callback' ? 'text-violet-400' : 'text-amber-400'}>{e.status === 'callback' ? 'Callback saved' : 'On hold'}</span>
        </div>))}
    </div>
  );
};

// Flow settings apply live; they auto-save shortly after each edit.
function useAutoSaveSettings(api, subAccountId, flow, onFlowUpdated) {
  const [s, setS] = useState({});
  const [name, setName] = useState('');
  const [state, setState] = useState('idle');
  const base = useRef(null);
  const fresh = useRef(false);
  useEffect(() => { setS(flow?.settings || {}); setName(flow?.name || ''); base.current = JSON.stringify([flow?.settings || {}, flow?.name || '']); fresh.current = true; }, [flow?.flow_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const sig = JSON.stringify([s, name]);
    if (fresh.current) { fresh.current = false; return undefined; }
    if (!flow?.flow_id || base.current === null || sig === base.current) return undefined;
    setState('saving');
    const t = setTimeout(async () => {
      try {
        const r = await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flow.flow_id}`, { settings: s, name });
        base.current = sig; onFlowUpdated?.(r.data); setState('saved');
      } catch { setState('idle'); toast.error('Could not save settings'); }
    }, 900);
    return () => clearTimeout(t);
  }, [s, name]); // eslint-disable-line react-hooks/exhaustive-deps
  return { s, setS, name, setName, state };
}

function SectionPanel({ section, api, subAccountId, flow, agents, onFlowUpdated, onRestored, versionKey }) {
  const { s, setS, name, setName, state } = useAutoSaveSettings(api, subAccountId, flow, onFlowUpdated);
  const voiceAgents = (agents || []).filter(a => (a.type || 'voice') !== 'chat' && (a.name || '').trim());
  const ov = s.override || {};
  const setOv = (patch) => setS(p => ({ ...p, override: { ...(p.override || {}), ...patch } }));
  const panel = {
    general: (<div className="space-y-4"><div><Label>Flow name</Label><input value={name} onChange={e => setName(e.target.value)} className={input} style={inputStyle} data-testid="rail-flow-name" />
      <Muted>Pick the IVR agent and call limit on the Start step.</Muted></div>
      <div className="rounded-xl border p-3" style={{ borderColor: ov.enabled ? 'rgb(251 113 133 / .5)' : 'var(--vox-chrome-border)' }}>
        <Switch on={!!ov.enabled} onChange={v => setOv({ enabled: v })} label="Emergency override" testid="rail-override-toggle" />
        <Muted>Instantly sends every call to the message or agent below, e.g. closed for weather.</Muted>
        {ov.enabled && <div className="space-y-2 mt-2">
          <textarea rows={2} value={ov.message || ''} onChange={e => setOv({ message: e.target.value })} placeholder="We're closed today due to weather. Please call back tomorrow." className={input} style={inputStyle} data-testid="rail-override-message" />
          <select value={ov.agent_id || ''} onChange={e => setOv({ agent_id: e.target.value })} className={input} style={inputStyle} data-testid="rail-override-agent">
            <option value="">Play the message and hang up</option>{voiceAgents.map(a => <option key={a.agent_id} value={a.agent_id}>Send to {a.name}</option>)}</select></div>}
      </div></div>),
    failsafe: (<div><Label>If anything fails, send callers to</Label>
      <select value={s.failsafe_agent_id || ''} onChange={e => setS(p => ({ ...p, failsafe_agent_id: e.target.value }))} className={input} style={inputStyle} data-testid="rail-failsafe-agent">
        <option value="">The IVR agent on the Start step</option>
        {voiceAgents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
      </select>
      <Muted>Used when a branch isn't connected, nobody answers, or the queue's max wait passes, so callers are never just dropped.</Muted></div>),
    live: flow?.flow_id ? <><LiveQueue api={api} subAccountId={subAccountId} flowId={flow.flow_id} /><Stats api={api} subAccountId={subAccountId} flowId={flow.flow_id} /></> : null,
    versions: flow?.flow_id ? <Versions api={api} subAccountId={subAccountId} flowId={flow.flow_id} onRestored={onRestored} refreshKey={versionKey} /> : null,
  };
  const current = SECTIONS.find(x => x.id === section);
  return (
    <aside className="w-[340px] border-l overflow-y-auto flex flex-col" style={{ background: 'var(--vox-chrome-bg)', borderColor: 'var(--vox-chrome-border)' }} data-testid={`rail-panel-${section}`}>
      <div className="px-4 py-3 border-b flex items-center" style={{ borderColor: 'var(--vox-chrome-border)' }}>
        <h3 className="text-sm font-semibold flex-1" style={{ color: 'var(--vox-text-primary)' }}>{current.label}</h3>
        {['general', 'failsafe'].includes(section) && state !== 'idle' && (
          <span className="text-[10px] flex items-center gap-1" style={{ color: 'var(--vox-text-muted)' }} data-testid="rail-save-state">
            {state === 'saving' ? <><Loader2 size={10} className="animate-spin" />Saving…</> : <><CheckCircle2 size={10} className="text-emerald-400" />Saved · live now</>}
          </span>)}
      </div>
      <div className="p-4 flex-1">{panel[section]}</div>
    </aside>
  );
}

export default function FlowSettingsRail({ selectedPanel, onTab, ...props }) {
  const [section, setSection] = useState('steps');
  const pick = (id) => { setSection(id); onTab?.(); };
  return (
    <div className="flex flex-shrink-0" data-testid="flow-settings-rail">
      {selectedPanel || (section === 'steps' ? <NodePalette /> : <SectionPanel section={section} {...props} />)}
      <nav className="w-[68px] flex flex-col items-center gap-1 py-3 border-l" style={{ background: 'var(--vox-chrome-bg)', borderColor: 'var(--vox-chrome-border)' }}>
        {SECTIONS.map(({ id, label, icon: Icon }) => {
          const active = !selectedPanel && section === id;
          return (
            <button key={id} onClick={() => pick(id)} title={label} data-testid={`rail-tab-${id}`}
              className={`w-14 py-2 rounded-xl flex flex-col items-center gap-1 transition-colors ${active ? 'bg-teal-400/15 text-teal-400' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
              style={active ? {} : { color: 'var(--vox-text-secondary)' }}>
              <Icon size={16} /><span className="text-[9px] font-medium leading-tight text-center">{label}</span>
            </button>);
        })}
      </nav>
    </div>
  );
}
